import React, { useState, useEffect } from "react";
import { SeparateLine, ButtonRow } from "styles/elements/main";
import { FormGroup, FormStyle } from "styles/forms/main";
import styled from "styled-components";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Button from "components/button";
import Api from "lib/api";
import { checkObjectValues } from "utils/checkEmptyValues";
import trackAnalytics from "trackAnalytics";
import Spinner from "components/Spinner";
import { GenericError, Input, SelectWithSearch } from "components";
import { useSelector } from "react-redux";
import { getUserRoles } from "modules/auth/slice";
import { getAllBoxTypes, getAllSuppliers, getAllTruckTypes } from "../api";
import { mapTruckDataToApi } from "../config/mapUiToData";
// eslint-disable-next-line import/no-cycle
import { LicenseInfo, TruckInfo } from ".";

export const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 18px;
    color: #006bb4;
`;
const Container = styled.div`
    margin-bottom: 27px;
`;
function CreateTruck({ onClose, onSuccess, setIsTruckDataChange }) {
    const [newTruckData, setNewTruckData] = useState({
        truckClass: null,
        truckType: null,
        year: null,
        plateNumber: null,
        licenseFace: null,
        licenseBack: null,
        licenseExpirationDate: null,
        supplierName: null,
        suppliers: [],
        photo: null,
        notes: null,
    });
    const [allTruckClass, setTruckClass] = useState(null);
    const [allSuppliers, setAllSuppliers] = useState(null);
    const [allBoxesType, setAllBoxesType] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [errors, setErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [licenseExpirationError, setLicenseExpirationError] = useState(null);
    const [plateNumberError, setPlateNumberError] = useState(null);
    const getCurrentUserRoles = useSelector(getUserRoles());
    const isSupplierPartner = getCurrentUserRoles?.includes("supplier_partner_account_manager");
    const clearTruckData = () => {
        setNewTruckData({
            truckClass: null,
            truckType: null,
            year: null,
            plateNumber: null,
            licenseFace: null,
            licenseBack: null,
            licenseExpirationDate: null,
            supplierName: null,
            suppliers: [],
            photo: null,
            notes: null,
        });
    };

    const handleGetTruckClass = async () => {
        const data = await getAllTruckTypes();
        setTruckClass(
            data.map((name) => ({
                value: newTruckData.truckType?.value,
                label: name,
            }))
        );
    };
    const handleGetSuppliers = async () => {
        const data = await getAllSuppliers();
        setAllSuppliers(data);
    };

    const handleGetBoxTypes = async (truckClass) => {
        const data = await getAllBoxTypes(truckClass);
        setAllBoxesType(data);
    };

    const handleSubmitTruck = async (e) => {
        e.preventDefault();
        trackAnalytics(`Truck_profile_submit_creation`);
        try {
            setIsLoading(true);
            console.log(mapTruckDataToApi(newTruckData), "d");
            const response = await Api.post(
                "trucks",
                await mapTruckDataToApi(newTruckData, "", isSupplierPartner)
            );
            setIsLoading(false);
            onSuccess(newTruckData.plateNumber);
            clearTruckData();
        } catch (error) {
            setIsLoading(false);
            setErrors(error?.errors);
            if (errors.license_expiration_date !== null) {
                setLicenseExpirationError(error.errors?.license_expiration_date?.[0]);
            }
            if (errors.plate_number !== null) {
                setPlateNumberError(error.errors?.plate_number?.[0]);
            }
        }
    };

    useEffect(() => {
        if (newTruckData?.truckClass) {
            handleGetBoxTypes(newTruckData.truckClass.label);
        } else {
            setAllBoxesType([]);
        }
    }, [newTruckData?.truckClass]);

    useEffect(() => {
        handleGetTruckClass();
        handleGetSuppliers();
    }, []);

    useEffect(() => {
        if (
            newTruckData.truckClass === undefined ||
            newTruckData.truckType === null ||
            newTruckData?.plateNumber === "" ||
            newTruckData.year == null ||
            newTruckData.licenseFace?.length === 0 ||
            newTruckData.licenseBack?.length === 0 ||
            newTruckData.licenseExpirationDate == null
        ) {
            setIsFormValid(false);
        } else {
            setIsFormValid(true);
        }
    }, [newTruckData]);

    useEffect(() => {
        if (checkObjectValues(newTruckData)) {
            setIsTruckDataChange(true);
        } else {
            setIsTruckDataChange(false);
        }
    }, [newTruckData]);

    useEffect(() => {
        setLicenseExpirationError(null);
    }, [newTruckData.licenseExpirationDate]);

    useEffect(() => {
        setPlateNumberError(null);
    }, [newTruckData.plateNumber]);

    return (
        <FormStyle>
            <SectionTitle>Truck Info</SectionTitle>
            <TruckInfo
                newTruckData={newTruckData}
                setNewTruckData={setNewTruckData}
                allTruckClass={allTruckClass}
                allBoxesType={allBoxesType}
                plateNumberError={plateNumberError}
                isEditable
            />
            <SeparateLine />

            <SectionTitle>License Info</SectionTitle>
            <LicenseInfo
                newTruckData={newTruckData}
                setNewTruckData={setNewTruckData}
                licenseExpirationError={licenseExpirationError}
                isEditable
            />
            <SeparateLine />
            <SectionTitle>Supplier Info</SectionTitle>
            <FormGroup>
                <SelectWithSearch
                    label="Supplier"
                    isEditable
                    isMulti={isSupplierPartner}
                    options={allSuppliers}
                    handleChange={(value) =>
                        setNewTruckData({
                            ...newTruckData,
                            ...(!isSupplierPartner
                                ? { supplierName: value }
                                : { suppliers: value }),
                        })
                    }
                    handleFilter={(filter) => getAllSuppliers(filter)}
                    value={!isSupplierPartner ? newTruckData.supplierName : newTruckData?.suppliers}
                />
            </FormGroup>
            <SeparateLine />
            <SectionTitle>Others</SectionTitle>
            <Container>
                <Input
                    label="Notes"
                    isEditable
                    placeholder="write your notes"
                    onChange={(e) =>
                        setNewTruckData({
                            ...newTruckData,
                            notes: e.target.value,
                        })
                    }
                    value={newTruckData.notes}
                />
            </Container>
            {errors && <GenericError errorMassege={errors} />}

            <ButtonRow>
                <Button
                    type="button"
                    onChange={onClose}
                    name="Cancel"
                    btnStyle="secondBtn"
                />
                <Button
                    type="submit"
                    name={isLoading ? <Spinner /> : "Add"}
                    onChange={handleSubmitTruck}
                    btnStyle={isFormValid ? "primaryBtn" : "disabled"}
                    disabled={!isFormValid}
                    icon={<AddCircleOutlineOutlinedIcon />}
                />
            </ButtonRow>
        </FormStyle>
    );
}

export default CreateTruck;
